import Udesk from 'Udesk';
import React, { FC, memo, useState } from 'react';
import { Select, Input, Space, Radio } from 'udesk-ui';
import OuterApiConfigsSelect from 'src/components/common/OuterApiConfigsSelect';
import OuterApiConfigsResponseSelect from 'src/components/common/OuterApiConfigsResponseSelect';
import RandomFieldSelect, { typeList_common } from 'src/components/common/RandomFieldSelect';
import InfoEntitySelect from 'src/components/common/InfoEntitySelect';
import { FieldFoundResponse, InformationEntity } from 'src/api/types';
import UdeskLocales from 'UdeskLocales';

const filedStyle = {
    width: 150,
};

const OperatorMap = {
    EQUALS: 'equals',
    CONTAINS: 'contains',
};

const KeyType = {
    INFOENTITY: 1,
    RANDOMFIELD: 2,
    FIXEDVALUE: 3,
};

interface TemplateProps {
    value?: {
        apiId?: number;
        responseKey?: string;
        operator?: string;
        keyType?: number;
        associateField?: FieldFoundResponse;
        entity?: InformationEntity;
        value?: string;
        defaultHit?: number;
    };
    onChange?: (v: TemplateProps['value']) => void;
    disabled?: boolean;
    source?: number;
    lang?: number;
}

export const Template: FC<TemplateProps> = memo((props) => {
    const { value, onChange, disabled = false, source, lang } = props;

    const {
        apiId,
        responseKey,
        operator,
        keyType,
        entity,
        associateField,
        value: fixedValue,
        defaultHit,
    } = value || {};

    const onInterfaceChange = (v) => {
        onChange?.({
            ...value,
            apiId: v,
        });
    };

    const onResponseChange = (v) => {
        onChange?.({
            ...value,
            responseKey: v,
        });
    };

    const [operatorOptions] = useState([
        {
            label: /* 等于 */ UdeskLocales['current'].components.checkPointOperatorListGather
                .externalInterfaceCheck.normal.equalTo,
            value: OperatorMap.EQUALS,
        },
        {
            label: /* 包含 */ UdeskLocales['current'].components.checkPointOperatorListGather
                .externalInterfaceCheck.normal.contain,
            value: OperatorMap.CONTAINS,
        },
    ]);

    const onOperatorChange = (v) => {
        onChange?.({
            ...value,
            operator: v,
        });
    };

    const [keyTypeOptions] = useState([
        {
            label: /* 信息实体 */ UdeskLocales['current'].components.checkPointOperatorListGather
                .externalInterfaceCheck.normal.informationEntity,
            value: KeyType.INFOENTITY,
        },
        {
            label: /* 随路字段 */ UdeskLocales['current'].components.checkPointOperatorListGather
                .externalInterfaceCheck.normal.followthepathField,
            value: KeyType.RANDOMFIELD,
        },
        {
            label: /* 固定值 */ UdeskLocales['current'].components.checkPointOperatorListGather
                .externalInterfaceCheck.normal.fixedValue,
            value: KeyType.FIXEDVALUE,
        },
    ]);

    const onKeyTypeChange = (v) => {
        onChange?.({
            ...value,
            keyType: v,
            value: undefined,
        });
    };

    const onRandomFieldChange = (v) => {
        onChange?.({
            ...value,
            associateField: v,
        });
    };

    const onEntityChange = (v) => {
        onChange?.({
            ...value,
            entity: v,
        });
    };

    const onFixedValueChange = (v) => {
        onChange?.({
            ...value,
            value: v,
        });
    };

    const onFallbackChange = (v) => {
        onChange?.({
            ...value,
            defaultHit: v,
        });
    };

    return (
        <Space style={{ marginLeft: 8 }} wrap>
            <OuterApiConfigsSelect
                value={apiId}
                onChange={onInterfaceChange}
                style={filedStyle}
                disabled={disabled}
            />

            <OuterApiConfigsResponseSelect
                id={apiId}
                value={responseKey}
                onChange={onResponseChange}
                style={filedStyle}
                disabled={disabled}
            />

            <Select
                options={operatorOptions}
                value={operator}
                onChange={onOperatorChange}
                style={filedStyle}
                disabled={disabled}
                placeholder={
                    /* 请选择操作符 */ UdeskLocales['current'].components
                        .checkPointOperatorListGather.externalInterfaceCheck.normal
                        .pleaseSelectAnOperator
                }
            />

            <Select
                options={keyTypeOptions}
                value={keyType}
                onChange={onKeyTypeChange}
                style={filedStyle}
                disabled={disabled}
                placeholder={
                    /* 请选择数据类型 */ UdeskLocales['current'].components
                        .checkPointOperatorListGather.externalInterfaceCheck.normal
                        .pleaseSelectADataType
                }
            />

            {keyType === KeyType.RANDOMFIELD ? (
                <RandomFieldSelect
                    value={associateField}
                    onChange={(v) =>onRandomFieldChange(v)}
                    sourceList={source}
                    typeList={typeList_common}
                    disabled={disabled}
                    style={filedStyle}
                    placeholder={
                        /* 请选择 */ UdeskLocales['current'].components.checkPointOperatorListGather
                            .externalInterfaceCheck.normal.pleaseSelect +
                        Udesk.enums.inspectDataSources.getName(source) +
                        /* 随路字段 */ UdeskLocales['current'].components
                            .checkPointOperatorListGather.externalInterfaceCheck.normal
                            .followthepathField
                    }
                    lang={lang}
                />
            ) : keyType === KeyType.INFOENTITY ? (
                <InfoEntitySelect
                    value={entity}
                    onChange={(v) => onEntityChange(v)}
                    style={filedStyle}
                    disabled={disabled}
                    lang={lang}
                />
            ) : keyType === KeyType.FIXEDVALUE ? (
                <Input
                    value={fixedValue as string}
                    onChange={(e) => onFixedValueChange(e.target.value)}
                    style={filedStyle}
                    disabled={disabled}
                />
            ) : null}

            {operator ? (
                <Space align="baseline" size="small">
                    <span>
                        {/* 无返回结果时 */}
                        {
                            UdeskLocales['current'].components.checkPointOperatorListGather
                                .externalInterfaceCheck.normal.whenNoResultIsReturned
                        }
                    </span>
                    <Radio.Group
                        value={defaultHit}
                        onChange={(e) => onFallbackChange(e.target.value)}
                        options={[
                            {
                                label: /* 结果为“是” */ UdeskLocales['current'].components
                                    .checkPointOperatorListGather.externalInterfaceCheck.normal
                                    .theResultIsYes,
                                value: 1,
                            },
                            {
                                label: /* 结果为“否” */ UdeskLocales['current'].components
                                    .checkPointOperatorListGather.externalInterfaceCheck.normal
                                    .resultIsNo,
                                value: 2,
                            },
                        ]}
                    />
                </Space>
            ) : null}
        </Space>
    );
});
